import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Dropdown, DropdownMenuItemType } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SelectableCardList, getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';
import { getAvailableGoalTypes, getSubMenuConfig } from '../../shared/config';
import { cali18n, getGoalTypeContents, getSubmenuCalloutOptions } from '../../shared/contants';
import { GoalTypeDropdownOption } from './goal-type-dropdown-option';

const ALERT_TYPE = {
  NO_ALERT: 0,
  SWITCH_TO_DESTINATION_FROM_EVENT: 1,
  SWITCH_TO_EVENT_FROM_DESTINATION: 2,
};

const GoalTypeViewUnstyled = (props) => {
  const {
    selectedObjective,
    updateGoalSelection,
    classes,
    permissions,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const { isEditScenario } = wizardContext;
  const [selectedCategory, setSelectedCategory] = useState(wizardContext.goalContext ? wizardContext.goalContext.GoalCategory : undefined);
  const [selectedGoalType, setSelectedGoalType] = useState(wizardContext.goalContext ? wizardContext.goalContext.Type : undefined);
  const defaultGoalTypes = selectedCategory ? getAvailableGoalTypes(selectedObjective, selectedCategory, wizardContext.permissions) : [];
  const subMenuConfig = getSubMenuConfig(wizardContext.permissions);
  const [availableTypes, setAvailableTypes] = useState(defaultGoalTypes);
  const submenuCalloutOptions = useMemo(() => getSubmenuCalloutOptions(i18n), [i18n]);
  const [alertType, setAlertType] = useState(ALERT_TYPE.NO_ALERT);

  const dropdownOptions = useMemo(() => {
    const options = [];
    // Build a multi-section menu from each item in the config.
    _.each(Object.keys(subMenuConfig), (headerKey, index) => {
      let addedOptionToSection = false;
      options.push({ key: headerKey, text: i18n.getString(cali18n(headerKey)), itemType: DropdownMenuItemType.Header });
      _.each(subMenuConfig[headerKey], (optionKey) => {
        const goalTypeList = getAvailableGoalTypes(selectedObjective, optionKey, wizardContext.permissions);
        if (isEditScenario && (selectedObjective === 'import' || selectedGoalType === 'ProductConversionGoal')) {
          if (goalTypeList.indexOf(selectedGoalType) > -1) {
            // Only add an option if current goal type is in available types.
            const keyText = i18n.getString(cali18n(optionKey));
            options.push({
              key: optionKey, text: keyText, ariaLabel: keyText, title: '', calloutOption: submenuCalloutOptions[optionKey],
            });
            addedOptionToSection = true;
          }
        } else if (goalTypeList.length > 0) {
          // Only add an option if it has at least one goal type for this objective.
          const keyText = i18n.getString(cali18n(optionKey));
          options.push({
            key: optionKey, text: keyText, ariaLabel: keyText, title: '', calloutOption: submenuCalloutOptions[optionKey],
          });
          addedOptionToSection = true;
        }
      });
      if (addedOptionToSection) {
        // Add a divider at the end of the section
        options.push({
          key: `divider_${index}`, ariaLabel: 'divider', text: '-', title: '', itemType: DropdownMenuItemType.Divider,
        });
      } else {
        options.pop(); // Remove the header if the section is empty
      }
    });
    options.pop(); // Remove trailing divider
    return options;
  }, [subMenuConfig, i18n, selectedObjective, wizardContext.permissions, isEditScenario, selectedGoalType, submenuCalloutOptions]);

  const onChangeCategory = useCallback((ev, option) => {
    setSelectedCategory(option.key);

    const updatedAvailableTypes = getAvailableGoalTypes(selectedObjective, option.key, wizardContext.permissions);
    setAvailableTypes(updatedAvailableTypes);
    // goal type should update after goal category is changed to not applicable categories
    updateGoalSelection({ GoalCategory: option.key, Type: selectedGoalType && updatedAvailableTypes.indexOf(selectedGoalType) < 0 ? undefined : selectedGoalType });
  }, [selectedGoalType, selectedObjective, updateGoalSelection, wizardContext.permissions]);

  const setAlertTypeIfClarityEventIsDefined = useCallback(() => {
    const isClarityTagSetupAllowed = permissions && _.result(permissions.dynamic, 'IsClarityTagSetupAllowed');
    if (!isClarityTagSetupAllowed || !wizardContext.goalData.ClarityEventDefinitionId) {
      return;
    }
    if (wizardContext.goalData.Type === 'EventGoal' && selectedGoalType === 'DestinationGoal') {
      setAlertType(ALERT_TYPE.SWITCH_TO_DESTINATION_FROM_EVENT);
    } else if (wizardContext.goalData.Type === 'DestinationGoal' && selectedGoalType === 'EventGoal') {
      setAlertType(ALERT_TYPE.SWITCH_TO_EVENT_FROM_DESTINATION);
    } else {
      setAlertType(ALERT_TYPE.NO_ALERT);
    }
  }, [permissions, selectedGoalType, wizardContext.goalData.ClarityEventDefinitionId, wizardContext.goalData.Type]);

  useEffect(() => {
    setAlertTypeIfClarityEventIsDefined();
  }, [selectedGoalType, setAlertTypeIfClarityEventIsDefined]);

  const onChangeType = useCallback((id) => {
    setSelectedGoalType(id);
    updateGoalSelection({ Type: id });
  }, [updateGoalSelection]);

  const goalTypeContents = useMemo(() => getGoalTypeContents(availableTypes, i18n, isEditScenario, selectedGoalType), [availableTypes, i18n, isEditScenario, selectedGoalType]);

  return (
    <div>
      <div className={classNames(classes.section, 'goal-category-selection')}>
        <div className={classes.nonAccordionTitle}>
          <span className="title">{i18n.getString(_TL_('Goal Category'))}</span>
          <HelpButton
            aria-label={i18n.getString(_TL_('Goal Category'))}
            topic="pop_BA_ConversionGoal_GoalCategory"
          />
        </div>
        <p className={classes.bodyText}>{i18n.getString(_TL_('You can use these categories to segment your performance data.'))}</p>
        <Dropdown
          ariaLabel={i18n.getString(_TL_('Goal Category dropdown'))}
          selectedKey={selectedCategory}
          onChange={onChangeCategory}
          placeholder={i18n.getString(_TL_('Select goal category'))}
          options={dropdownOptions}
          styles={{
            dropdown: { width: 'fit-content', minWidth: 300 },
            dropdownItem: {
              width: '100%', height: '100%', display: 'block', paddingLeft: 0, paddingRight: 0,
            },
            dropdownItemSelected: {
              width: '100%', height: '100%', display: 'block', paddingLeft: 0, paddingRight: 0,
            },
          }}
          onRenderOption={option => <GoalTypeDropdownOption option={option} />}
        />
        {selectedCategory === 'None' && (
          <div className="row" role="alert">
            <p className={classes.errorText}>{i18n.getString(_TL_('Select a goal category to continue. This is a new, required setting.'))}</p>
          </div>
        )}
      </div>

      {!_.isEmpty(selectedCategory) &&
        <div className={classNames(classes.section, 'goal-type-selection')}>
          <p className={classes.nonAccordionTitle}>{i18n.getString(_TL_('Goal Type'))}</p>
          <p className={classes.bodyText}>{i18n.getString(_TL_('Select the type of interaction you would like to track with this goal.'))}</p>
          <SelectableCardList
            contents={goalTypeContents}
            onItemSelected={onChangeType}
            selectedItem={selectedGoalType}
          />
        </div>}
      {alertType === ALERT_TYPE.SWITCH_TO_EVENT_FROM_DESTINATION &&
        <div className="alert alert-info">
          <span className="alert-image" />
          <span>
            {i18n.getString(_TL_('You\'ll need to customize your UET tag tracking code if you change your goal type to Custom event.'))}
          </span>
        </div>
        }
      {alertType === ALERT_TYPE.SWITCH_TO_DESTINATION_FROM_EVENT &&
      <div className="alert alert-info">
        <span className="alert-image" />
        <span>
          {i18n.getString(_TL_('We\'ve removed the event tracking you previously set up because your goal type is Destination URL.'))}
          <LearnMoreLink
            linkText={i18n.getString(_TL_('Learn about URL tracking in Microsoft Advertising.'))}
            topic="app54821"
          />
        </span>
      </div>
        }
    </div>
  );
};

GoalTypeViewUnstyled.propTypes = {
  selectedObjective: PropTypes.string.isRequired,
  updateGoalSelection: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
};

export const GoalTypeView = withDefaultStyles(GoalTypeViewUnstyled, getStepStyles);
